import { Menu, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Navigate, useNavigate } from "@tanstack/react-router";
import { Content } from "shared/components/global/Content";
import { FloatingActionButton } from "shared/components/global/FloatingActionButton/FloatingActionButton";
import { LoadMoreTrigger } from "shared/components/global/LoadMoreTrigger";
import { IpawsAlertCard } from "shared/components/ipaws/alert/IpawsAlertCard";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { usePermissionCheck } from "shared/stores/oidc";
import { runAsync, to } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { createOffsetBasedGqlQueryOptions } from "shared/utils/query";
import { NoMessagesFigure } from "src/assets/NoMessagesFigure";
import { graphql } from "src/gql";
import { Permission } from "src/gql/graphql";
import { queryClient } from "src/queryClient";

const NoMessageText = () => {
    const hasCreatePermission = usePermissionCheck(Permission.IpawsAlertModify);
    if (hasCreatePermission)
        return (
            <Text ta={"center"}>
                Looks like you haven't drafted any IPAWS alerts yet, you can&nbsp;
                <Link to="/ipaws/alerts/new">
                    <Text span c="brand">
                        click here
                    </Text>
                </Link>
                &nbsp;to get started!
            </Text>
        );
    return <Text ta={"center"}>Looks like you haven't drafted any IPAWS alerts yet.</Text>;
};

const Page = () => {
    const { data, refetch } = useSuspenseInfiniteQuery(queries.drafts);
    const drafts = data.pages.flat();
    const discard = useGraphqlMutation(muts.discard);
    const createTemplate = useGraphqlMutation(muts.createTemplate);
    const nav = useNavigate();
    const canViewAlerts = usePermissionCheck(Permission.IpawsHistoryView);
    const hasIpawsCreate = usePermissionCheck(Permission.IpawsAlertModify);

    if (!canViewAlerts) return <Navigate to="/ipaws" />;

    async function handleCreateTemplate(draftId: string) {
        await notify.confirm("Are you sure you want to create a new IPAWS alert template from this draft?", {
            title: "Confirm",
        });

        await createTemplate
            .mutateAsync({ id: draftId })
            .then(() => nav({ to: "/ipaws/templates" }))
            .then(modals.closeAll);
    }

    return (
        <Content>
            <Content.Heading>IPAWS Alert Drafts</Content.Heading>

            {drafts.length == 0 && (
                <NoMessagesFigure>
                    <NoMessageText />
                </NoMessagesFigure>
            )}

            <Stack>
                {drafts.map((each) => (
                    <IpawsAlertCard key={each.id} data={each}>
                        <IpawsAlertCard.PrimaryAction
                            onClick={() => void nav({ to: "/ipaws/drafts/$draftId", params: { draftId: each.id } })}
                        >
                            Edit
                        </IpawsAlertCard.PrimaryAction>
                        <IpawsAlertCard.Action
                            onClick={() =>
                                runAsync(async () => {
                                    const { close } = await notify.confirm("Are you sure you want to discard this draft?", {
                                        title: "Discard Draft",
                                        label: "Discard",
                                    });

                                    await discard.mutateAsync({ id: each.id });
                                    await refetch();

                                    close();
                                })
                            }
                        >
                            Discard
                        </IpawsAlertCard.Action>
                        <IpawsAlertCard.Action
                            onClick={() => {
                                handleCreateTemplate(each.id).catch(notify.catch);
                            }}
                        >
                            Create Template
                        </IpawsAlertCard.Action>
                    </IpawsAlertCard>
                ))}
            </Stack>

            <LoadMoreTrigger query={queries.drafts} />

            {hasIpawsCreate && (
                <FloatingActionButton>
                    <Menu.Item component={Link} to="/ipaws/alerts/new">
                        New IPAWS Alert
                    </Menu.Item>
                </FloatingActionButton>
            )}
        </Content>
    );
};

const queries = {
    drafts: createOffsetBasedGqlQueryOptions({
        queryKey: qk("ipaws", "drafts"),
        document: graphql(`
            query IpawsDrafts($offset: Int!, $limit: Int!) {
                ipawsAlerts(status: [DRAFT], limit: $limit, offset: $offset) {
                    ...IpawsAlertThumbnail
                }
            }
        `),
        select: to("ipawsAlerts"),
    }),
};

const muts = {
    discard: graphql(`
        mutation IpawsDiscardDraft($id: UUID!) {
            deleteDraftIpawsAlert(id: $id) {
                id
            }
        }
    `),
    createTemplate: graphql(`
        mutation IpawsCreateTemplateFromDraft($id: UUID!) {
            createIpawsTemplateFromAlert(alertId: $id) {
                id
            }
        }
    `),
};

/**
 * @public
 */
export const Route = createFileRoute("/_auth/ipaws/drafts/")({
    component: Page,
    async beforeLoad() {
        await queryClient.prefetchInfiniteQuery(queries.drafts);
    },
});
